import { supabase } from './supabaseClient';
import { 
  sendEmail, 
  sendRegistrationEmail,
  getRegistrationEmailTemplate, 
  getBookingConfirmationTemplate 
} from './emailService';
import { z } from 'zod';
import { parsePhoneNumber } from 'libphonenumber-js';
import bcrypt from 'bcryptjs';

// Types per il profilo utente esteso
export interface UserProfile {
  id: string;
  phone_number?: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  newsletter_subscribed: boolean;
  full_name?: string;
  date_of_birth?: string;
  nationality?: string;
  document_type?: 'carta_identita' | 'passaporto' | 'patente';
  document_front_url?: string;
  document_back_url?: string;
  selfie_url?: string;
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  two_factor_enabled: boolean;
  security_question?: string;
  security_answer_hash?: string;
  bio?: string;
  profile_image_url?: string;
  preferences: any;
  facebook_verified: boolean;
  google_verified: boolean;
  linkedin_verified: boolean;
  identity_verified: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  registration_phase: 1 | 2 | 3;
  verification_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

// Types per prenotazioni
export interface Booking {
  id: string;
  user_id: string;
  accommodation_id: number;
  check_in: string;
  check_out: string;
  guests_count: number;
  base_price: number;
  cleaning_fee: number;
  service_fee: number;
  taxes: number;
  total_price: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'paid' | 'refunded';
  guest_name: string;
  guest_email: string;
  guest_phone?: string;
  special_requests?: string;
  host_notes?: string;
  airbnb_booking_id?: string;
  avaibook_booking_id?: string;
  external_source: 'direct' | 'airbnb' | 'avaibook';
  created_at: string;
  updated_at: string;
}

export interface BookingWithAccommodation extends Booking {
  accommodations?: {
    title_it: string;
    image: string;
  };
}

// Schema di validazione per la registrazione Fase 1
export const registrationPhase1Schema = z.object({
  email: z.string().email('Email non valida'),
  phone: z.string().min(10, 'Numero di telefono non valido'),
  password: z.string().min(8, 'Password deve essere almeno 8 caratteri'),
  confirmPassword: z.string(),
  termsAccepted: z.boolean().refine(val => val === true, 'Devi accettare i termini'),
  privacyAccepted: z.boolean().refine(val => val === true, 'Devi accettare la privacy policy'),
  newsletterSubscribed: z.boolean().optional()
}).refine(data => data.password === data.confirmPassword, {
  message: "Le password non corrispondono",
  path: ["confirmPassword"]
});

// Schema di validazione per la registrazione Fase 2
export const registrationPhase2Schema = z.object({
  fullName: z.string().min(2, 'Nome completo richiesto'),
  dateOfBirth: z.string().refine(date => {
    const birthDate = new Date(date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    return age >= 18;
  }, 'Devi avere almeno 18 anni'),
  nationality: z.string().min(2, 'Nazionalità richiesta'),
  documentType: z.enum(['carta_identita', 'passaporto', 'patente']).optional(),
  addressStreet: z.string().optional(),
  addressCity: z.string().optional(),
  addressPostalCode: z.string().optional(),
  addressCountry: z.string().optional()
});

// FUNZIONI DI REGISTRAZIONE E AUTENTICAZIONE

// Registrazione Fase 1 - Creazione account base
export const registerPhase1 = async (data: z.infer<typeof registrationPhase1Schema>) => {
  try {
    console.log('🚀 Inizio registrazione fase 1');
    
    // Valida i dati
    const validatedData = registrationPhase1Schema.parse(data);
    console.log('✅ Dati validati correttamente');

    // Valida il numero di telefono
    const phoneNumber = parsePhoneNumber(validatedData.phone, 'IT');
    
    if (!phoneNumber.isValid()) {
      return { 
        success: false, 
        error: 'Numero di telefono non valido' 
      };
    }

    // Registrazione con Supabase Auth (con verifica email automatica)
    console.log('Tentativo registrazione con email:', validatedData.email);
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: validatedData.email,
      password: validatedData.password,
      options: {
        data: {
          full_name: '', // Verrà aggiornato nella fase 2
        }
        // Rimuoviamo temporaneamente emailRedirectTo fino a configurazione corretta
        // emailRedirectTo: `${window.location.origin}/verify-email?confirmed=true`
      }
    });

    console.log('Risultato auth:', authData);
    console.log('Errore Auth Supabase:', authError);

    if (authError) {
      // Gestione specifica per rate limit
      if (authError.message.includes('rate limit') || authError.message.includes('email rate limit')) {
        return { 
          success: false, 
          error: 'Troppi tentativi di registrazione. Attendi qualche minuto prima di riprovare, oppure controlla se hai già un account esistente.' 
        };
      }
      
      // Gestione altri errori di auth
      if (authError.message.includes('already registered') || authError.message.includes('User already registered')) {
        return { 
          success: false, 
          error: 'Questo indirizzo email è già registrato. Prova ad accedere.' 
        };
      }

      return { 
        success: false, 
        error: authError.message || 'Errore durante la registrazione' 
      };
    }

    if (!authData.user) {
      return { 
        success: false, 
        error: 'Errore durante la creazione dell\'account' 
      };
    }

    // Nota: Supabase invierà automaticamente l'email di verifica
    console.log('Email di verifica inviata automaticamente da Supabase');

    // Verifica che l'utente esista effettivamente
    const { data: profile, error: profileError } = await supabase
      .from('user_profiles')
      .insert([
        { 
          id: authData.user.id,
          email: validatedData.email,
          phone_number: phoneNumber.formatInternational(),
          terms_accepted: validatedData.termsAccepted,
          privacy_accepted: validatedData.privacyAccepted,
          newsletter_subscribed: validatedData.newsletterSubscribed || true,
          registration_phase: 1,
          email_verified: false,
          phone_verified: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (profileError) {
      console.error('Errore creazione profilo:', profileError);
      // Non bloccare per errori di profilo, l'importante è che l'auth sia avvenuta
    }

    console.log('Profilo creato con successo');

    return { 
      success: true, 
      user: authData.user,
      message: 'Registrazione completata! Controlla la tua email per verificare l\'account.'
    };

  } catch (error) {
    console.error('Errore registrazione fase 1:', error);
    
    // Gestione errori specifici anche nei catch
    if (error instanceof Error && error.message.includes('rate limit')) {
      return { 
        success: false, 
        error: 'Troppi tentativi di registrazione. Attendi qualche minuto prima di riprovare.' 
      };
    }
    
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore durante la registrazione' 
    };
  }
};

// Registrazione Fase 2 - Completamento profilo
export const registerPhase2 = async (data: z.infer<typeof registrationPhase2Schema>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      // Se l'utente non è autenticato, significa che deve ancora confermare l'email
      return { 
        success: false, 
        error: 'Devi prima confermare la tua email per continuare. Controlla la tua casella di posta.' 
      };
    }

    // Valida i dati
    const validatedData = registrationPhase2Schema.parse(data);

    // Aggiorna profilo utente
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        full_name: validatedData.fullName,
        date_of_birth: validatedData.dateOfBirth,
        nationality: validatedData.nationality,
        document_type: validatedData.documentType,
        address_street: validatedData.addressStreet,
        address_city: validatedData.addressCity,
        address_postal_code: validatedData.addressPostalCode,
        address_country: validatedData.addressCountry,
        registration_phase: 2
      })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return { success: true, message: 'Profilo aggiornato con successo!' };
  } catch (error) {
    console.error('Errore registrazione fase 2:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto' 
    };
  }
};

// Upload documenti identità
export const uploadIdentityDocument = async (
  file: File, 
  documentType: 'document_front' | 'document_back' | 'selfie'
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Upload file
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${documentType}_${Date.now()}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('documents')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    // Ottieni URL pubblico
    const { data: { publicUrl } } = supabase
      .storage
      .from('documents')
      .getPublicUrl(uploadData.path);

    // Salva riferimento nel database
    const { error: dbError } = await supabase
      .from('uploaded_documents')
      .insert({
        user_id: user.id,
        document_type: documentType,
        file_url: publicUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type
      });

    if (dbError) throw dbError;

    // Aggiorna profilo utente
    const updateField = `${documentType}_url`;
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({ [updateField]: publicUrl })
      .eq('id', user.id);

    if (profileError) throw profileError;

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Errore upload documento:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore upload' 
    };
  }
};

// FUNZIONI PER PROFILO UTENTE

// Ottieni profilo utente completo
export const getUserProfile = async (): Promise<UserProfile | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Errore recupero profilo:', error);
    return null;
  }
};

// Aggiorna profilo utente
export const updateUserProfile = async (updates: Partial<UserProfile>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento profilo:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore aggiornamento' 
    };
  }
};

// FUNZIONI PER PRENOTAZIONI

// Verifica disponibilità unificata (considerando tutti i canali)
export const checkAvailability = async (
  accommodationId: string, 
  checkIn: string, 
  checkOut: string
): Promise<{ available: boolean; price?: number; message?: string }> => {
  try {
    // 1. Verifica prenotazioni dirette esistenti
    const { data: existingBookings, error: bookingsError } = await supabase
      .from('bookings')
      .select('*')
      .eq('accommodation_id', accommodationId)
      .neq('status', 'cancelled')
      .or(`and(check_in.lte.${checkOut},check_out.gt.${checkIn})`);

    if (bookingsError) throw bookingsError;

    // 2. Verifica prenotazioni esterne (Airbnb, AvaiBook)
    const { data: externalBookings, error: externalError } = await supabase
      .from('external_bookings')
      .select('*')
      .eq('accommodation_id', accommodationId)
      .neq('status', 'cancelled')
      .or(`and(check_in.lte.${checkOut},check_out.gt.${checkIn})`);

    if (externalError && externalError.code !== 'PGRST116') { // Ignore table not found
      console.warn('Tabella external_bookings non trovata, crearla se necessario');
    }

    // 3. Verifica blocchi di disponibilità
    const { data: availability, error: availError } = await supabase
      .from('availability')
      .select('*')
      .eq('accommodation_id', accommodationId)
      .gte('date', checkIn)
      .lt('date', checkOut);

    if (availError) throw availError;

    // 4. Controlla se ci sono conflitti
    const hasDirectBookingConflict = existingBookings && existingBookings.length > 0;
    const hasExternalBookingConflict = externalBookings && externalBookings.length > 0;

    if (hasDirectBookingConflict) {
      return { 
        available: false, 
        message: 'Date già prenotate tramite prenotazione diretta' 
      };
    }

    if (hasExternalBookingConflict) {
      return { 
        available: false, 
        message: 'Date già prenotate su altro canale (Airbnb/AvaiBook)' 
      };
    }

    // 5. Verifica disponibilità giornaliera
    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    // Controlla ogni giorno
    const unavailableDays = availability?.filter(day => !day.available) || [];
    if (unavailableDays.length > 0) {
      return { 
        available: false, 
        message: `Date non disponibili: ${unavailableDays.map(d => new Date(d.date).toLocaleDateString()).join(', ')}` 
      };
    }

    // 6. Calcola prezzo totale
    const totalPrice = availability?.reduce((sum, day) => sum + (day.price_override || 0), 0) || 0;

    // 7. Trigger sincronizzazione se necessario (non bloccante)
    triggerSyncIfNeeded(accommodationId);

    return { available: true, price: totalPrice };
  } catch (error) {
    console.error('Errore verifica disponibilità:', error);
    return { available: false, message: 'Errore nel controllo disponibilità' };
  }
};

// Funzione helper per trigger sincronizzazione asincrona
const triggerSyncIfNeeded = async (accommodationId: string) => {
  try {
    // Controlla quando è stata l'ultima sincronizzazione
    const { data: lastSync } = await supabase
      .from('sync_logs')
      .select('*')
      .eq('accommodation_id', accommodationId)
      .order('created_at', { ascending: false })
      .limit(1);

    const lastSyncTime = lastSync?.[0]?.created_at;
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    // Se l'ultima sincronizzazione è più vecchia di 1 ora, triggera una nuova sync
    if (!lastSyncTime || new Date(lastSyncTime) < oneHourAgo) {
      // Importa dinamicamente per evitare dipendenze circolari
      const { channelSyncManager } = await import('./channelSyncManager');
      
      // Sincronizzazione asincrona (non bloccante)
      channelSyncManager.syncAllChannels([accommodationId]).catch(error => {
        console.error('Errore sincronizzazione asincrona:', error);
      });
    }
  } catch (error) {
    console.error('Errore trigger sincronizzazione:', error);
  }
};

// Ottieni prenotazioni utente con dettagli alloggio
export const getUserBookings = async (): Promise<BookingWithAccommodation[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        accommodations (
          title_it,
          image
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Errore nel recuperare le prenotazioni:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('Errore imprevisto in getUserBookings:', error);
    return [];
  }
};

// Crea prenotazione
export const createBooking = async (bookingData: {
  accommodationId: string;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  specialRequests?: string;
}): Promise<{ success: boolean; booking?: Booking; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Verifica disponibilità
    const availability = await checkAvailability(
      bookingData.accommodationId, 
      bookingData.checkIn, 
      bookingData.checkOut
    );

    if (!availability.available) {
      throw new Error('Date non disponibili');
    }

    // Calcola prezzi
    const basePrice = availability.price || 0;
    const cleaningFee = basePrice * 0.1; // 10% del prezzo base
    const serviceFee = basePrice * 0.05; // 5% del prezzo base
    const taxes = basePrice * 0.1; // 10% di tasse
    const totalPrice = basePrice + cleaningFee + serviceFee + taxes;

    // Crea prenotazione
    const { data: booking, error } = await supabase
      .from('bookings')
      .insert({
        user_id: user.id,
        accommodation_id: bookingData.accommodationId,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests_count: bookingData.guestsCount,
        base_price: basePrice,
        cleaning_fee: cleaningFee,
        service_fee: serviceFee,
        taxes: taxes,
        total_price: totalPrice,
        guest_name: bookingData.guestName,
        guest_email: bookingData.guestEmail,
        guest_phone: bookingData.guestPhone,
        special_requests: bookingData.specialRequests,
        external_source: 'direct',
        status: 'pending',
        payment_status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, booking };
  } catch (error) {
    console.error('Errore creazione prenotazione:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore creazione prenotazione' 
    };
  }
};

// Conferma prenotazione (dopo pagamento)
export const confirmBooking = async (bookingId: string) => {
  try {
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (fetchError) throw fetchError;

    // Aggiorna status prenotazione
    const { error: updateError } = await supabase
      .from('bookings')
      .update({
        status: 'confirmed',
        payment_status: 'paid'
      })
      .eq('id', bookingId);

    if (updateError) throw updateError;

    // Invia email di conferma
    const emailTemplate = getBookingConfirmationTemplate({
      guestName: booking.guest_name,
      accommodationTitle: 'Alloggio Confermato', // TODO: fetch dal database
      checkIn: new Date(booking.check_in).toLocaleDateString('it-IT'),
      checkOut: new Date(booking.check_out).toLocaleDateString('it-IT'),
      totalPrice: `€${booking.total_price.toFixed(2)}`,
      bookingId: booking.id
    });

    await sendEmail(booking.guest_email, emailTemplate);

    return { success: true };
  } catch (error) {
    console.error('Errore conferma prenotazione:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore conferma prenotazione' 
    };
  }
};

// FUNZIONI DI VERIFICA E SICUREZZA

// Verifica email
export const verifyEmail = async (token: string) => {
  try {
    // TODO: Implementare logica di verifica email con token
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non trovato');

    const { error } = await supabase
      .from('user_profiles')
      .update({ email_verified: true })
      .eq('id', user.id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore verifica email:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore verifica email' 
    };
  }
};

// Genera e invia codice SMS per verifica telefono
export const sendPhoneVerificationCode = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    const profile = await getUserProfile();
    if (!profile?.phone_number) throw new Error('Numero di telefono non trovato');

    // Genera codice 6 cifre
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    
    // Salva codice nel database (con scadenza)
    const { error } = await supabase
      .from('verification_codes')
      .insert({
        user_id: user.id,
        code: await bcrypt.hash(code, 10),
        type: 'phone',
        expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString() // 10 minuti
      });

    if (error) throw error;

    // TODO: Inviare SMS con servizio terzo (Twilio, etc.)
    console.log('Codice verifica SMS:', code); // Per testing

    return { success: true, message: 'Codice inviato via SMS' };
  } catch (error) {
    console.error('Errore invio codice SMS:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore invio codice' 
    };
  }
};

// Verifica codice SMS
export const verifyPhoneCode = async (code: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Verifica codice dal database
    const { data: verificationCodes, error: fetchError } = await supabase
      .from('verification_codes')
      .select('*')
      .eq('user_id', user.id)
      .eq('type', 'phone')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1);

    if (fetchError) throw fetchError;
    if (!verificationCodes || verificationCodes.length === 0) {
      throw new Error('Codice scaduto o non trovato');
    }

    const isValid = await bcrypt.compare(code, verificationCodes[0].code);
    if (!isValid) throw new Error('Codice non valido');

    // Aggiorna profilo utente
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ phone_verified: true })
      .eq('id', user.id);

    if (updateError) throw updateError;

    // Elimina codice usato
    await supabase
      .from('verification_codes')
      .delete()
      .eq('id', verificationCodes[0].id);

    return { success: true, message: 'Telefono verificato con successo!' };
  } catch (error) {
    console.error('Errore verifica codice:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore verifica codice' 
    };
  }
};

// Cancella prenotazione
export const cancelBooking = async (bookingId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Verifica che la prenotazione appartenga all'utente
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('user_id, status')
      .eq('id', bookingId)
      .single();

    if (fetchError) throw new Error('Prenotazione non trovata');
    
    if (booking.user_id !== user.id) {
      throw new Error('Non hai i permessi per cancellare questa prenotazione');
    }

    if (booking.status === 'cancelled') {
      throw new Error('La prenotazione è già stata cancellata');
    }

    if (booking.status === 'completed') {
      throw new Error('Non puoi cancellare una prenotazione già completata');
    }

    // Aggiorna lo stato della prenotazione
    const { error: updateError } = await supabase
      .from('bookings')
      .update({ 
        status: 'cancelled',
        updated_at: new Date().toISOString()
      })
      .eq('id', bookingId);

    if (updateError) throw updateError;

    return { success: true };
  } catch (error) {
    console.error('Errore cancellazione prenotazione:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore cancellazione prenotazione' 
    };
  }
};

// FUNZIONI PER SERVIZI

// Interface per i servizi
export interface Service {
  id: string;
  provider_id: string;
  title_it: string;
  title_en: string;
  description_it?: string;
  description_en?: string;
  category: string;
  duration_minutes?: number;
  max_participants: number;
  price_per_person?: number;
  price_per_group?: number;
  images?: string[];
  location?: string;
  requirements?: string;
  cancellation_policy?: string;
  status: 'active' | 'inactive' | 'draft';
  created_at: string;
  updated_at: string;
}

// Interface per le richieste servizi
export interface ServiceRequest {
  id: string;
  service_id: string;
  provider_id: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  requested_date?: string;
  requested_time?: string;
  preferred_time_slots?: string[];
  participants_count: number;
  message?: string;
  status: 'pending' | 'contacted' | 'confirmed' | 'cancelled' | 'completed';
  admin_notes?: string;
  provider_notes?: string;
  created_at: string;
  updated_at: string;
}

// Ottieni tutti i servizi attivi
export const getActiveServices = async (): Promise<Service[]> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('status', 'active')
      .order('title_it');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Errore recupero servizi:', error);
    return [];
  }
};

// Ottieni servizi per categoria
export const getServicesByCategory = async (category: string): Promise<Service[]> => {
  try {
    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('status', 'active')
      .eq('category', category)
      .order('title_it');

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Errore recupero servizi per categoria:', error);
    return [];
  }
};

// Crea richiesta servizio
export const createServiceRequest = async (requestData: {
  serviceId: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  requestedDate?: string;
  requestedTime?: string;
  preferredTimeSlots?: string[];
  participantsCount: number;
  message?: string;
}): Promise<{ success: boolean; request?: ServiceRequest; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Ottieni i dati del servizio per recuperare il provider_id
    const { data: service, error: serviceError } = await supabase
      .from('services')
      .select('provider_id')
      .eq('id', requestData.serviceId)
      .single();

    if (serviceError || !service) {
      throw new Error('Servizio non trovato');
    }

    const { data: request, error } = await supabase
      .from('service_requests')
      .insert({
        service_id: requestData.serviceId,
        provider_id: service.provider_id,
        client_name: requestData.clientName,
        client_email: requestData.clientEmail,
        client_phone: requestData.clientPhone,
        requested_date: requestData.requestedDate,
        requested_time: requestData.requestedTime,
        preferred_time_slots: requestData.preferredTimeSlots,
        participants_count: requestData.participantsCount,
        message: requestData.message,
        status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, request };
  } catch (error) {
    console.error('Errore creazione richiesta servizio:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore creazione richiesta servizio' 
    };
  }
};

// Ottieni richieste servizi dell'utente
export const getUserServiceRequests = async (): Promise<ServiceRequest[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('service_requests')
      .select(`
        *,
        services (
          title_it,
          title_en,
          category
        )
      `)
      .eq('client_email', user.email)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Errore recupero richieste servizi utente:', error);
    return [];
  }
}; 